/**
 * =============================================================================
 * REPLY FORM COMPONENT - GAP-006
 * =============================================================================
 *
 * Form component for submitting replies to questions on the storefront.
 * Placed below ReplyList in the QuestionDetailView.
 *
 * SPEC REFERENCE: GAP-006-widget-reply-ui-SPEC.md §3.3
 *
 * Features:
 * - Body textarea with character count
 * - Name and email fields with localStorage prefill
 * - Turnstile CAPTCHA verification
 * - Client-side validation mirroring server rules
 * - Success state with "pending review" messaging
 * - Error state with sanitized messages and form data preserved
 * - Offline detection via useOnlineStatus
 * - Accessible form controls (WCAG AA)
 *
 * =============================================================================
 */

import React, { useState, useCallback, useId, useEffect, useRef } from 'react';
import { CONTENT_VALIDATION, FILE_VALIDATION } from '@find-my-part/types';
import { widgetApi } from '../../api/client';
import { ImageUploader } from '../ImageUploader';
import { ImageUploadFailureNotice } from '../ImageUploadFailureNotice';
import { TurnstileWidget, type TurnstileWidgetRef } from '../TurnstileWidget';
import { OfflineNotice } from '../OfflineNotice';
import { useStyleInjection, useOnlineStatus, useAnnounce } from '../../hooks';
import { useReplySubmission } from '../../hooks/useReplySubmission';
import type { ReplyFormData } from '../../hooks/useReplySubmission';
import { buildPartialUploadMessage } from '../../lib/imageUpload';
import { logger } from '../../lib/logger';
import { BREAKPOINTS } from '../../tokens';

// =============================================================================
// TYPES
// =============================================================================

export interface ReplyFormProps {
  /** Question ID to submit the reply to */
  questionId: string;
  /** Callback when reply is successfully submitted */
  onSuccess?: (replyId: string) => void;
  /** Callback on submission error */
  onError?: (error: Error) => void;
  /** Whether to start with the form collapsed behind a CTA button */
  defaultCollapsed?: boolean;
  /** Additional CSS class */
  className?: string;
  /**
   * Pre-resolved Turnstile site key.
   *
   * The ADR-028 Slice 2B `question-actions` island resolves the public config
   * BEFORE it commits live controls over the slot's static fallback, so that a
   * config failure can never replace a usable static affordance with a form
   * that cannot submit. Passing the already-resolved key here skips this
   * component's own fetch, keeping the canonical detail page at ONE
   * `/api/public/config` request. Omitted everywhere else, so the legacy
   * detail path keeps its existing self-fetching behaviour unchanged.
   */
  turnstileSiteKey?: string;
}

interface ReplyFormErrors {
  body?: string;
  authorName?: string;
  authorEmail?: string;
  turnstile?: string;
  consentToPublicDisplay?: string; // BL-PRIVACY-CONSENT-CAPTURE-001
}

// =============================================================================
// CONSTANTS
// =============================================================================

const REPLY_AUTHOR_STORAGE_KEY = 'fmp_reply_author';

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Validate email format using a simple regex.
 * Intentionally permissive — server does final validation.
 */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Validate all reply form fields.
 * Mirrors server-side validation rules from public.ts.
 */
function validateReplyForm(data: ReplyFormData): ReplyFormErrors {
  const errors: ReplyFormErrors = {};

  const bodyTrimmed = data.body.trim();
  if (!bodyTrimmed) {
    errors.body = 'Reply is required';
  } else if (bodyTrimmed.length < CONTENT_VALIDATION.MIN_REPLY_LENGTH) {
    errors.body = `Reply must be at least ${CONTENT_VALIDATION.MIN_REPLY_LENGTH} characters`;
  } else if (bodyTrimmed.length > CONTENT_VALIDATION.MAX_REPLY_LENGTH) {
    errors.body = `Reply must be at most ${CONTENT_VALIDATION.MAX_REPLY_LENGTH.toLocaleString()} characters`;
  }

  const nameTrimmed = data.authorName.trim();
  if (!nameTrimmed) {
    errors.authorName = 'Name is required';
  }

  const emailTrimmed = data.authorEmail.trim();
  if (!emailTrimmed) {
    errors.authorEmail = 'Email is required';
  } else if (!isValidEmail(emailTrimmed)) {
    errors.authorEmail = 'Please enter a valid email address';
  }

  // Public-display consent validation (BL-PRIVACY-CONSENT-CAPTURE-001)
  if (!data.consentToPublicDisplay) {
    errors.consentToPublicDisplay = 'Please confirm you agree to public display before submitting';
  }

  return errors;
}

/**
 * Load saved author info from localStorage with resilience.
 * Returns null if data is unavailable, corrupted, or wrong schema.
 */
function loadSavedAuthor(): { authorName: string; authorEmail: string } | null {
  try {
    const raw = localStorage.getItem(REPLY_AUTHOR_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.authorName === 'string' &&
      typeof parsed.authorEmail === 'string'
    ) {
      return { authorName: parsed.authorName, authorEmail: parsed.authorEmail };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Save author info to localStorage after successful submission.
 */
function saveAuthor(authorName: string, authorEmail: string): void {
  try {
    localStorage.setItem(REPLY_AUTHOR_STORAGE_KEY, JSON.stringify({ authorName, authorEmail }));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

// =============================================================================
// CHARACTER COUNT
// =============================================================================

interface CharacterCountProps {
  current: number;
  min: number;
  max: number;
  id?: string;
}

function CharacterCount({ current, min, max, id }: CharacterCountProps): React.ReactElement {
  const remaining = max - current;
  const percentUsed = (current / max) * 100;

  let stateClass = '';
  let statusText = `${current} / ${max.toLocaleString()}`;

  if (current > 0 && current < min) {
    stateClass = 'fmp-reply-form__char-count--warning';
    const needed = min - current;
    statusText = `${current} / ${max.toLocaleString()} (${needed} more needed)`;
  } else if (remaining <= 0) {
    stateClass = 'fmp-reply-form__char-count--exceeded';
    statusText = `${current} / ${max.toLocaleString()} (limit reached)`;
  } else if (percentUsed >= 90) {
    stateClass = 'fmp-reply-form__char-count--warning';
    statusText = `${current} / ${max.toLocaleString()} (${remaining} remaining)`;
  }

  return (
    <span
      id={id}
      className={`fmp-reply-form__char-count ${stateClass}`}
      aria-live="polite"
      aria-atomic="true"
    >
      {statusText}
    </span>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Reply submission form for the storefront widget.
 *
 * Appears below the reply list on the QuestionDetailView.
 * Includes Turnstile CAPTCHA verification for spam protection.
 *
 * @example
 * ```tsx
 * <ReplyForm
 *   questionId="q_abc123xyz789"
 *   onSuccess={(replyId) => console.log(`Reply ${replyId} submitted`)}
 * />
 * ```
 */
export function ReplyForm({
  questionId,
  onSuccess,
  onError,
  defaultCollapsed = false,
  className = '',
  turnstileSiteKey: providedTurnstileSiteKey,
}: ReplyFormProps): React.ReactElement {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  // Form data state — initialize with localStorage prefill
  const [formData, setFormData] = useState<ReplyFormData>(() => {
    const saved = loadSavedAuthor();
    return {
      body: '',
      authorName: saved?.authorName ?? '',
      authorEmail: saved?.authorEmail ?? '',
      consentToPublicDisplay: false, // BL-PRIVACY-CONSENT-CAPTURE-001
    };
  });

  // Selected images (managed by ImageUploader). PR5-remainder: a customer can
  // attach photos to a reply (e.g. the part photo staff asked for in an RFI).
  const [images, setImages] = useState<File[]>([]);

  // Validation errors
  const [errors, setErrors] = useState<ReplyFormErrors>({});

  // Turnstile state
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(
    providedTurnstileSiteKey ?? null
  );
  const [turnstileLoading, setTurnstileLoading] = useState(providedTurnstileSiteKey === undefined);
  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  // First error field ref for focus management
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const errorAlertRef = useRef<HTMLDivElement>(null);

  // Generate unique IDs for form fields
  const formId = useId();
  const bodyId = `${formId}-body`;
  const nameId = `${formId}-name`;
  const emailId = `${formId}-email`;
  const consentId = `${formId}-consent`; // BL-PRIVACY-CONSENT-CAPTURE-001
  // Composite (non-native-control) field group labels for the ImageUploader and
  // Turnstile widgets, which have no single labelable form control. Exposed via
  // role="group" + aria-labelledby instead of an orphan <label>
  // (BL-MSPL-WIDGET-A11Y-LABEL-001).
  const imagesLabelId = `${formId}-images-label`;
  const turnstileLabelId = `${formId}-turnstile-label`;

  // Inject styles
  useStyleInjection(replyFormStyles, 'fmp-reply-form-styles');

  // Online status
  const isOnline = useOnlineStatus();

  // Screen reader announcements
  const { announce, clear, Announcer } = useAnnounce();
  const announcementInFlightRef = useRef(false);

  // Submission hook
  const handleSubmitSuccess = useCallback(
    (replyId: string) => {
      // Save author info for future prefill
      saveAuthor(formData.authorName.trim(), formData.authorEmail.trim());
      onSuccess?.(replyId);
    },
    [formData.authorName, formData.authorEmail, onSuccess]
  );

  const handleSubmitError = useCallback(
    (error: Error) => {
      // Reset Turnstile for retry
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      onError?.(error);
    },
    [onError]
  );

  const {
    submit,
    status,
    error: submitError,
    reset: resetSubmission,
    failedImages,
    isRetrying,
    uploadProgress,
    retryFailedImages,
    continueWithoutImages,
  } = useReplySubmission({
    questionId,
    onSuccess: handleSubmitSuccess,
    onError: handleSubmitError,
  });

  // Fetch Turnstile site key.
  // Skipped when a caller (the Slice 2B question-actions island) already
  // resolved it as part of its pre-commit readiness gate.
  useEffect(() => {
    if (providedTurnstileSiteKey !== undefined) return;
    let mounted = true;

    const fetchConfig = async (): Promise<void> => {
      try {
        const response = await widgetApi.config.get();
        if (mounted && response.success) {
          setTurnstileSiteKey(response.data.turnstileSiteKey);
        }
      } catch (error) {
        logger.error('CONFIG_LOAD_FAILURE', error instanceof Error ? error : null);
      } finally {
        if (mounted) {
          setTurnstileLoading(false);
        }
      }
    };

    if (widgetApi.isInitialized()) {
      fetchConfig();
    } else {
      setTurnstileLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, [providedTurnstileSiteKey]);

  // Focus success message when status changes to success
  useEffect(() => {
    if (status === 'success') {
      successRef.current?.focus();
    }
  }, [status]);

  // Focus error alert when submission error occurs
  useEffect(() => {
    if (status === 'error' && submitError) {
      errorAlertRef.current?.focus();
    }
  }, [status, submitError]);

  // Turnstile handlers
  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
    setErrors((prev) => ({ ...prev, turnstile: undefined }));
  }, []);

  const handleTurnstileError = useCallback((error: string) => {
    setTurnstileToken(null);
    setErrors((prev) => ({ ...prev, turnstile: error }));
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
    setErrors((prev) => ({
      ...prev,
      turnstile: 'Verification expired. Please complete the security check again.',
    }));
  }, []);

  // Input change handler
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => {
        const hasFieldError = prev[name as keyof ReplyFormErrors];
        if (hasFieldError) {
          return { ...prev, [name]: undefined };
        }
        return prev;
      });
    },
    []
  );

  // Public-display consent checkbox handler (BL-PRIVACY-CONSENT-CAPTURE-001).
  // Separate from handleChange because a checkbox carries `checked`, not `value`.
  const handleConsentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setFormData((prev) => ({ ...prev, consentToPublicDisplay: checked }));
    setErrors((prev) =>
      prev.consentToPublicDisplay ? { ...prev, consentToPublicDisplay: undefined } : prev
    );
  }, []);

  // Focus first error field
  const focusFirstError = useCallback((validationErrors: ReplyFormErrors) => {
    if (validationErrors.body) {
      bodyRef.current?.focus();
    } else if (validationErrors.authorName) {
      nameRef.current?.focus();
    } else if (validationErrors.authorEmail) {
      emailRef.current?.focus();
    }
  }, []);

  // Form submit handler
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      // A same-tick programmatic duplicate must not clear or replace the
      // announcement owned by the accepted in-flight submission.
      if (announcementInFlightRef.current) return;

      const validationErrors = validateReplyForm(formData);

      if (!turnstileToken) {
        validationErrors.turnstile = 'Please complete the verification';
      }

      const errorCount = Object.keys(validationErrors).length;
      if (errorCount > 0) {
        setErrors(validationErrors);
        focusFirstError(validationErrors);
        announce(
          `${errorCount} error${errorCount > 1 ? 's' : ''} found. Please correct and try again.`,
          'assertive'
        );
        return;
      }

      setErrors({});
      announcementInFlightRef.current = true;
      clear('assertive');
      announce('Submitting reply...', 'polite');
      try {
        await submit(formData, turnstileToken!, images);
      } finally {
        // Terminal success/error/partial UI owns the outcome signal. Remove the
        // in-progress polite text before it can outlive that terminal state.
        clear('polite');
        announcementInFlightRef.current = false;
      }
    },
    [formData, images, turnstileToken, submit, focusFirstError, announce, clear]
  );

  // Reset form to idle state
  const handleReset = useCallback(() => {
    setFormData((prev) => ({
      body: '',
      authorName: prev.authorName,
      authorEmail: prev.authorEmail,
      consentToPublicDisplay: false, // BL-PRIVACY-CONSENT-CAPTURE-001
    }));
    setImages([]);
    setErrors({});
    setTurnstileToken(null);
    turnstileRef.current?.reset();
    resetSubmission();
    // Focus body textarea for next reply
    setTimeout(() => bodyRef.current?.focus(), 0);
  }, [resetSubmission]);

  // Dismiss error alert
  const handleDismissError = useCallback(() => {
    resetSubmission();
  }, [resetSubmission]);

  const isSubmitting = status === 'submitting';

  // Render success state
  if (status === 'success') {
    return (
      <>
        <Announcer />
        <div className={`fmp-reply-form fmp-reply-form--success ${className}`}>
          <div
            ref={successRef}
            className="fmp-reply-form__success"
            role="status"
            aria-live="polite"
            tabIndex={-1}
          >
            <svg
              className="fmp-reply-form__success-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9 12l2 2 4-4" />
            </svg>
            <h3 className="fmp-reply-form__success-title">Reply Submitted</h3>
            <p className="fmp-reply-form__success-message">
              Your reply has been submitted and is pending review. It will appear once approved by
              an administrator.
            </p>
            <button type="button" onClick={handleReset} className="fmp-btn fmp-btn--secondary">
              Submit Another Reply
            </button>
          </div>
        </div>
      </>
    );
  }

  // Partial-failure state — the reply was created and is pending review, but one
  // or more photos failed to attach. NOT the success screen and NOT onSuccess:
  // a recoverable, assertively-announced state with retry / continue actions
  // (BL-WIDGET-UPLOAD-FAILURE-UX-001), mirroring the question path.
  if (status === 'partial') {
    // The announcer remains the first child across every branch. Partial-state
    // controls own their visible live semantics; a later success is conveyed by
    // focus on its role=status panel rather than a duplicate announcement.
    return (
      <>
        <Announcer />
        <ImageUploadFailureNotice
          message={buildPartialUploadMessage('reply', failedImages.length, images.length)}
          failed={failedImages}
          onRetry={retryFailedImages}
          onContinue={continueWithoutImages}
          isRetrying={isRetrying}
        />
      </>
    );
  }

  // Collapsed state — show CTA button
  if (isCollapsed) {
    return (
      <>
        <Announcer />
        <div className={`fmp-reply-form fmp-reply-form--collapsed ${className}`}>
          <button
            type="button"
            className="fmp-reply-form__expand-btn"
            onClick={() => {
              setIsCollapsed(false);
              // Focus the body textarea after React renders the expanded form
              setTimeout(() => bodyRef.current?.focus(), 0);
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
              className="fmp-reply-form__expand-icon"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            Add Your Answer
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Announcer />
      <form
        onSubmit={handleSubmit}
        className={`fmp-reply-form ${className}`}
        noValidate
        aria-label="Reply form"
        aria-busy={isSubmitting}
      >
        <h3 className="fmp-reply-form__header">Leave a Reply</h3>

        {/* Offline Notice */}
        {!isOnline && <OfflineNotice />}

        {/* Submission Error Alert */}
        {submitError && (
          <div ref={errorAlertRef} className="fmp-reply-form__alert" role="alert" tabIndex={-1}>
            <span>{submitError}</span>
            <button
              type="button"
              onClick={handleDismissError}
              className="fmp-reply-form__alert-dismiss"
              aria-label="Dismiss error"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Body Textarea */}
        <div className="fmp-reply-form__field">
          <label htmlFor={bodyId} className="fmp-reply-form__label">
            Your Reply{' '}
            <span className="fmp-reply-form__required" aria-hidden="true">
              *
            </span>
          </label>
          <textarea
            ref={bodyRef}
            id={bodyId}
            name="body"
            value={formData.body}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`fmp-reply-form__textarea ${errors.body ? 'fmp-reply-form__textarea--error' : ''}`}
            placeholder="Write your reply..."
            rows={4}
            aria-required="true"
            aria-invalid={!!errors.body}
            aria-describedby={
              [errors.body ? `${bodyId}-error` : null, `${bodyId}-count`]
                .filter(Boolean)
                .join(' ') || undefined
            }
            maxLength={CONTENT_VALIDATION.MAX_REPLY_LENGTH}
          />
          <div className="fmp-reply-form__field-meta">
            <CharacterCount
              current={formData.body.length}
              min={CONTENT_VALIDATION.MIN_REPLY_LENGTH}
              max={CONTENT_VALIDATION.MAX_REPLY_LENGTH}
              id={`${bodyId}-count`}
            />
          </div>
          {errors.body && (
            <p id={`${bodyId}-error`} className="fmp-reply-form__error" role="alert">
              {errors.body}
            </p>
          )}
        </div>

        {/* Photos (optional) — PR5-remainder. Reuses the question-path uploader,
            capped at the customer per-reply limit; the server chokepoint remains
            the authoritative gate. Composite label via role=group + aria-labelledby
            (BL-MSPL-WIDGET-A11Y-LABEL-001). */}
        <div className="fmp-reply-form__field" role="group" aria-labelledby={imagesLabelId}>
          <span id={imagesLabelId} className="fmp-reply-form__label">
            Add Photos
          </span>
          <ImageUploader
            images={images}
            onImagesChange={setImages}
            disabled={isSubmitting}
            maxImages={FILE_VALIDATION.MAX_IMAGES_PER_REPLY}
          />
        </div>

        {/* Name and Email Row */}
        <div className="fmp-reply-form__row">
          {/* Author Name */}
          <div className="fmp-reply-form__field">
            <label htmlFor={nameId} className="fmp-reply-form__label">
              Your Name{' '}
              <span className="fmp-reply-form__required" aria-hidden="true">
                *
              </span>
            </label>
            <input
              ref={nameRef}
              id={nameId}
              name="authorName"
              type="text"
              value={formData.authorName}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`fmp-reply-form__input ${errors.authorName ? 'fmp-reply-form__input--error' : ''}`}
              placeholder="Enter your name"
              aria-required="true"
              aria-invalid={!!errors.authorName}
              aria-describedby={errors.authorName ? `${nameId}-error` : undefined}
              maxLength={100}
            />
            {errors.authorName && (
              <p id={`${nameId}-error`} className="fmp-reply-form__error" role="alert">
                {errors.authorName}
              </p>
            )}
          </div>

          {/* Author Email */}
          <div className="fmp-reply-form__field">
            <label htmlFor={emailId} className="fmp-reply-form__label">
              Your Email{' '}
              <span className="fmp-reply-form__required" aria-hidden="true">
                *
              </span>
            </label>
            <input
              ref={emailRef}
              id={emailId}
              name="authorEmail"
              type="email"
              value={formData.authorEmail}
              onChange={handleChange}
              disabled={isSubmitting}
              className={`fmp-reply-form__input ${errors.authorEmail ? 'fmp-reply-form__input--error' : ''}`}
              placeholder="you@example.com"
              aria-required="true"
              aria-invalid={!!errors.authorEmail}
              aria-describedby={errors.authorEmail ? `${emailId}-error` : undefined}
            />
            {errors.authorEmail && (
              <p id={`${emailId}-error`} className="fmp-reply-form__error" role="alert">
                {errors.authorEmail}
              </p>
            )}
          </div>
        </div>

        {/* Turnstile Verification */}
        <div className="fmp-reply-form__field" role="group" aria-labelledby={turnstileLabelId}>
          <span id={turnstileLabelId} className="fmp-reply-form__label">
            Security Verification{' '}
            <span className="fmp-reply-form__required" aria-hidden="true">
              *
            </span>
          </span>
          {turnstileLoading ? (
            <div className="fmp-turnstile-placeholder">Loading verification...</div>
          ) : turnstileSiteKey ? (
            <TurnstileWidget
              ref={turnstileRef}
              siteKey={turnstileSiteKey}
              onVerify={handleTurnstileVerify}
              onError={handleTurnstileError}
              onExpire={handleTurnstileExpire}
              disabled={isSubmitting}
            />
          ) : (
            <div className="fmp-turnstile-placeholder fmp-turnstile-placeholder--error">
              Verification unavailable
            </div>
          )}
          {errors.turnstile && (
            <p className="fmp-reply-form__error" role="alert">
              {errors.turnstile}
            </p>
          )}
        </div>

        {/* Public-display consent (BL-PRIVACY-CONSENT-CAPTURE-001) */}
        <div className="fmp-reply-form__field">
          <label htmlFor={consentId} className="fmp-reply-form__consent">
            <input
              id={consentId}
              name="consentToPublicDisplay"
              type="checkbox"
              checked={formData.consentToPublicDisplay}
              onChange={handleConsentChange}
              disabled={isSubmitting}
              className="fmp-reply-form__consent-checkbox"
              aria-required="true"
              aria-invalid={!!errors.consentToPublicDisplay}
              aria-describedby={errors.consentToPublicDisplay ? `${consentId}-error` : undefined}
            />
            <span className="fmp-reply-form__consent-text">
              I agree that my reply and any photos I attach may be published publicly on this
              store&apos;s website to help other customers.{' '}
              <span className="fmp-reply-form__required" aria-hidden="true">
                *
              </span>
            </span>
          </label>
          {errors.consentToPublicDisplay && (
            <p id={`${consentId}-error`} className="fmp-reply-form__error" role="alert">
              {errors.consentToPublicDisplay}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div className="fmp-reply-form__actions">
          <button
            type="submit"
            disabled={isSubmitting || !isOnline || !turnstileToken}
            className="fmp-btn fmp-btn--primary"
          >
            {isSubmitting ? (
              <>
                <span className="fmp-btn__spinner" aria-hidden="true" />
                {uploadProgress
                  ? `Uploading photo ${uploadProgress.current} of ${uploadProgress.total}…`
                  : 'Submitting...'}
              </>
            ) : (
              'Submit Reply'
            )}
          </button>
        </div>

        {/* Determinate image-upload progress (a11y parity with the question
            path): a multi-photo upload on a slow/ITP connection otherwise reads
            as a hung "Submitting…". Only rendered while photos are uploading. */}
        {uploadProgress && (
          <div
            className="fmp-reply-form__progress"
            role="progressbar"
            aria-valuenow={uploadProgress.current}
            aria-valuemin={0}
            aria-valuemax={uploadProgress.total}
            aria-label="Photo upload progress"
          >
            <div
              className="fmp-reply-form__progress-bar"
              style={{
                width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
              }}
            />
          </div>
        )}
      </form>
    </>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const replyFormStyles = `
  :where(.fmp-widget-scope) .fmp-reply-form {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-lg);
    margin-top: var(--fmp-space-2xl);
    padding-top: var(--fmp-space-2xl);
    border-top: var(--fmp-border-width) solid var(--fmp-color-border);
    font-family: var(--fmp-font-family);
  }

  /* Collapsed state — just a CTA button */
  :where(.fmp-widget-scope) .fmp-reply-form--collapsed {
    display: flex;
    justify-content: center;
  }

  :where(.fmp-widget-scope) .fmp-reply-form__expand-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--fmp-space-sm);
    padding: var(--fmp-space-md) var(--fmp-space-2xl);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-control);
    background: var(--fmp-color-surface);
    color: var(--fmp-color-primary);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    font-family: inherit;
    cursor: pointer;
    transition: box-shadow var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__expand-btn:hover {
    border-color: var(--fmp-color-primary);
    box-shadow: var(--fmp-shadow-resting);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__expand-btn:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-primary);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__expand-icon {
    width: var(--fmp-size-18);
    height: var(--fmp-size-18);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__header {
    margin: 0;
    font-size: var(--fmp-font-size-lg);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__field {
    display: flex;
    flex-direction: column;
    gap: var(--fmp-size-6);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--fmp-space-lg);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__label {
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__required {
    color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__textarea,
  :where(.fmp-widget-scope) .fmp-reply-form__input {
    padding: var(--fmp-size-10) var(--fmp-space-md);
    border: var(--fmp-border-width) solid var(--fmp-color-border-light);
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
    font-family: inherit;
    transition: box-shadow var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__textarea:focus-visible,
  :where(.fmp-widget-scope) .fmp-reply-form__input:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
    border-color: var(--fmp-color-primary);
    box-shadow: var(--fmp-shadow-focus);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__textarea:disabled,
  :where(.fmp-widget-scope) .fmp-reply-form__input:disabled {
    background-color: var(--fmp-color-surface-muted);
    cursor: not-allowed;
  }

  :where(.fmp-widget-scope) .fmp-reply-form__textarea--error,
  :where(.fmp-widget-scope) .fmp-reply-form__input--error {
    border-color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__textarea--error:focus-visible,
  :where(.fmp-widget-scope) .fmp-reply-form__input--error:focus-visible {
    box-shadow: var(--fmp-shadow-focus-danger);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__textarea {
    resize: vertical;
    min-height: var(--fmp-size-100);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__field-meta {
    display: flex;
    justify-content: flex-end;
    margin-top: var(--fmp-space-xs);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__char-count {
    font-size: var(--fmp-font-size-xs);
    color: var(--fmp-color-text-muted);
    font-variant-numeric: tabular-nums;
  }

  :where(.fmp-widget-scope) .fmp-reply-form__char-count--warning {
    color: var(--fmp-color-warning-text);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__char-count--exceeded {
    color: var(--fmp-color-error);
    font-weight: var(--fmp-font-weight-semibold);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__error {
    max-inline-size: var(--fmp-layout-prose-max);
    margin: 0;
    font-size: var(--fmp-font-size-sm);
    color: var(--fmp-color-error);
  }

  /* Public-display consent (BL-PRIVACY-CONSENT-CAPTURE-001) */
  :where(.fmp-widget-scope) .fmp-reply-form__consent {
    display: flex;
    align-items: flex-start;
    gap: var(--fmp-size-10);
    cursor: pointer;
  }

  :where(.fmp-widget-scope) .fmp-reply-form__consent-checkbox {
    margin-top: var(--fmp-size-2);
    width: var(--fmp-size-16);
    height: var(--fmp-size-16);
    flex-shrink: 0;
    cursor: pointer;
  }

  :where(.fmp-widget-scope) .fmp-reply-form__consent-text {
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-sm);
    line-height: var(--fmp-line-height-body);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__actions {
    margin-top: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__progress {
    height: var(--fmp-size-4);
    margin-top: var(--fmp-space-sm);
    background-color: var(--fmp-color-progress-track);
    border-radius: var(--fmp-radius-sm);
    overflow: hidden;
  }

  :where(.fmp-widget-scope) .fmp-reply-form__progress-bar {
    height: 100%;
    background-color: var(--fmp-color-primary);
    transition: none;
  }

  @media (prefers-reduced-motion: reduce) {
    :where(.fmp-widget-scope) .fmp-reply-form__progress-bar {
      transition: none;
    }
  }

  :where(.fmp-widget-scope) .fmp-reply-form__alert {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--fmp-space-md);
    padding: var(--fmp-space-md) var(--fmp-space-lg);
    background-color: var(--fmp-color-error-bg);
    border: var(--fmp-border-width) solid var(--fmp-color-error-border);
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__alert > span {
    max-inline-size: var(--fmp-layout-prose-max);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__alert-dismiss {
    padding: var(--fmp-space-xs) var(--fmp-space-md);
    border: var(--fmp-border-width) solid var(--fmp-color-error);
    border-radius: var(--fmp-radius-sm);
    background: transparent;
    color: var(--fmp-color-error);
    font-size: var(--fmp-font-size-sm);
    cursor: pointer;
    white-space: nowrap;
  }

  :where(.fmp-widget-scope) .fmp-reply-form__alert-dismiss:hover {
    background-color: var(--fmp-color-error-bg);
  }

  :where(.fmp-widget-scope) .fmp-reply-form--success {
    margin-top: var(--fmp-space-2xl);
    padding-top: var(--fmp-space-2xl);
    border-top: var(--fmp-border-width) solid var(--fmp-color-border);
    font-family: var(--fmp-font-family);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__success {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: var(--fmp-space-section) var(--fmp-space-xl);
    gap: var(--fmp-space-md);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__success:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__success-icon {
    width: var(--fmp-size-48);
    height: var(--fmp-size-48);
    color: var(--fmp-color-success);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__success-title {
    margin: 0;
    font-size: var(--fmp-font-size-lg);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
  }

  :where(.fmp-widget-scope) .fmp-reply-form__success-message {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
    max-width: var(--fmp-size-400);
  }

  /* Mobile responsive */
  @media screen and (max-width: ${BREAKPOINTS.viewport.smallMax}) {
    :where(.fmp-widget-scope) .fmp-reply-form {
      gap: var(--fmp-space-md);
    }

    :where(.fmp-widget-scope) .fmp-reply-form__row {
      grid-template-columns: 1fr;
      gap: var(--fmp-space-md);
    }

    :where(.fmp-widget-scope) .fmp-reply-form__textarea,
    :where(.fmp-widget-scope) .fmp-reply-form__input {
      font-size: var(--fmp-font-size-base);
      padding: var(--fmp-space-md);
    }

    :where(.fmp-widget-scope) .fmp-reply-form__success {
      padding: var(--fmp-space-2xl) var(--fmp-space-lg);
    }
  }

  /* High contrast mode */
  @media (prefers-contrast: high) {
    :where(.fmp-widget-scope) .fmp-reply-form__textarea:focus-visible,
    :where(.fmp-widget-scope) .fmp-reply-form__input:focus-visible {
      outline: var(--fmp-outline-width) var(--fmp-outline-style) currentColor;
      outline-offset: var(--fmp-outline-offset);
      box-shadow: none;
    }
  }

  /* Reduced motion */
  @media (prefers-reduced-motion: reduce) {
    :where(.fmp-widget-scope) .fmp-reply-form__textarea,
    :where(.fmp-widget-scope) .fmp-reply-form__input {
      transition: none;
    }
  }
`;
